import { format } from 'date-fns';
import { Avatar, Tooltip } from '@mui/material';
import { formatHours } from '../../../utils/timeCalculation';
import formatDate from '../../../utils/formatDate';
import { stringAvatar } from '../../../utils/avatarCreator';
import { BILLABLE_OPTION } from '../../Report/constants';

export const boldHeader = (color) => (table) => <b style={{ color }}>{table?.column?.columnDef?.header}</b>;

const stripHTMLTags = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const truncateDescription = (description, maxLength) => {
  if (!description || description.length <= maxLength) return description;

  const plainText = stripHTMLTags(description);
  return `${plainText.substring(0, maxLength)}...`;
};

export const NAV_TABS = [
  {
    label: 'Board',
    path: '/mytask/board',
  },
  {
    label: 'List',
    path: '/mytask/table',
  },
];

export const OFFSET_HEIGHT = 200;

export const MY_TASK_LIST_TABLE_COLUMNS = [
  {
    accessorKey: 'taskName',
    header: 'Task',
    size: 30,
    Header: boldHeader('#454F5B'),
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue()}</span>,
  },
  // {
  //   accessorKey: 'description',
  //   header: 'Description',
  //   size: 30,
  //   enableSorting: false,
  //   Cell: ({ row }) => {
  //     const { description, maxLength = 10 } = row.original;
  //     const truncatedDescription = truncateDescription(description, maxLength);
  //     return <div style={{ textTransform: 'capitalize' }} dangerouslySetInnerHTML={{ __html: truncatedDescription }} />;
  //   },
  //   Header: boldHeader('#454F5B'),
  // },
  {
    accessorKey: 'taskType',
    header: 'Task Type',
    enableSorting: false,
    enableColumnFilter: false,
    Header: boldHeader('#454F5B'),
    Cell: ({ cell }) => <span>{cell.getValue() || 'N/A'}</span>,
  },
  {
    accessorKey: 'classification',
    header: 'Classification',
    enableSorting: false,
    enableColumnFilter: false,
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => (row?.original?.classification?.id ? row?.original?.classification.name : 'N/A'),
  },
  {
    accessorKey: 'user.userName',
    header: 'User',
    size: 30,
    Header: boldHeader('#454F5B'),
    Cell: ({ cell }) => (
      <div style={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 5 }}>
        {cell.getValue() ? (
          <Tooltip title={cell.getValue()} placement="top" arrow>
            <Avatar
              {...stringAvatar(cell.getValue())}
              sx={{
                ...stringAvatar(cell.getValue()).sx,
                width: 20,
                height: 20,
                fontSize: '0.75rem', // Adjust this value as needed
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Assign this task" placement="top" arrow>
            <Avatar sx={{ width: 20, height: 20 }} />
          </Tooltip>
        )}

        {cell.getValue()}
      </div>
    ),
  },
  {
    accessorKey: 'projects.projectName',
    header: 'Project',
    size: 30,
    Header: boldHeader('#454F5B'),
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue()}</span>,
  },
  {
    accessorKey: 'plannedHours',
    header: 'Estimated Hours',
    enableColumnFilter: true,
    Cell: (table) => <span>{table?.row?.original?.plannedHours ? table?.row?.original?.plannedHours : 'N/A'}</span>,
    size: 30,
    Header: boldHeader('#454F5B'),
  },
  {
    accessorKey: 'consumedHours',
    header: 'Consumed Hours',
    enableColumnFilter: false,
    Cell: (table) => (
      <span>{table?.row?.original?.consumedHours ? formatHours(table?.row?.original?.consumedHours) : 'N/A'}</span>
    ),
    size: 30,
    Header: boldHeader('#454F5B'),
  },
  {
    accessorFn: (row) => new Date(row.startDate),
    id: 'startDate',
    header: 'Start Date',
    enableColumnFilter: false,
    Cell: ({ row }) => (row.original.startDate ? formatDate(row.original.startDate) : 'N/A'),
    Header: boldHeader('#454F5B'),
    size: 30,
  },
  {
    accessorFn: (row) => new Date(row.endDate),
    id: 'endDate',
    header: 'End Date',
    enableColumnFilter: false,
    Cell: ({ row }) => (row.original.endDate ? formatDate(row.original.endDate) : 'N/A'),
    Header: boldHeader('#454F5B'),
    size: 30,
  },
  {
    accessorFn: (row) => new Date(row.startedAt),
    id: 'startedAt',
    header: 'Actual Start Date',
    enableColumnFilter: false,
    Cell: ({ row }) => (row.original.startedAt ? formatDate(row.original.startedAt) : 'N/A'),
    Header: boldHeader('#454F5B'),
    size: 30,
  },
  {
    accessorFn: (row) => new Date(row.completedAt),
    id: 'completedAt',
    header: 'Actual End Date',
    enableColumnFilter: false,
    Cell: ({ row }) => (row.original.completedAt ? formatDate(row.original.completedAt) : 'N/A'),
    Header: boldHeader('#454F5B'),
    size: 30,
  },
  {
    accessorKey: 'isBillable',
    header: 'Tasks Category',
    enableSorting: false,
    filterVariant: 'select',
    filterSelectOptions: BILLABLE_OPTION,
    Header: boldHeader('#454F5B'),
    Cell: ({row}) => <span>{row?.original?.isBillable ? 'Billable' : 'Non-Billable'}</span>,
  },
];

export const MY_TASK_LIST_TABLE_DEFAULT_PROPS = {
  positionActionsColumn: 'last',
  enableFullScreenToggle: false,
  enableColumnActions: false,
  manualPagination: true,
  manualFiltering: true,
  manualSorting: true,
  initialState: {
    density: 'compact', // or 'standard' or 'compact'
  }, 
  muiTablePaperProps: {
    sx: {
      color: 'rgb(71 85 105 / var(--tw-text-opacity))',
    },
  },
  muiTopToolbarProps: {
    sx: {
      backgroundColor: '#fff',
    },
  },
  muiTableHeadRowProps: {
    sx: {
      '--tw-bg-opacity': 1,
      backgroundColor: 'rgb(226 232 240 / var(--tw-bg-opacity))',
      fontWeight: '900',
    },
  },
  muiTableHeadCellFilterTextFieldProps: {
    sx: {
      m: '0.5rem 0',
      borderColor: 'white',
      '& input': {
        borderColor: 'white',
        '&::placeholder': {
          color: 'white',
        },
      },
      '& .MuiInput-root:before, &.MuiInputBase-root:before': {
        borderBottomColor: 'white',
        color: 'white',
      },
      '& .MuiInputBase-input, &.MuiButtonBase-root, &.MuiIconButton-root': {
        textTransform: 'capitalize',
        color: 'white',
      },
    },
  },
  muiTableHeadCellProps: {
    sx: {
      backgroundColor: '#F9FAFB',
      margin: 'auto',
      fontWeight: '800',
      fontSize: '.75rem',
      lineHeight: '2rem',
      textTransform: 'uppercase',
      '--tw-text-opacity': 1,
      borderColor: '#F9FAFB',
      paddingRight: '1.5rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.5rem',
      ' & .Mui-TableHeadCell-Content': {
        position: 'relative',
        top: '5px',
      },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      '--tw-border-opacity': 1,
      borderColor: 'rgb(241 245 249 / var(--tw-border-opacity))',
      '--tw-text-opacity': 1,
      paddingLeft: '1.5rem',
    },
  },
};
